import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { JjamesButtonComponent, JJamesCardComponent, Role } from 'shared-lib';
import { ParkingZoneService, InfrastructureService, DeviceGroupDto, BarrierService, BarrierDto, ListBarriersRequest, BarrierDtoPaginationResult, ParkingProcessService, ParkingProcessListItemDto, ListParkingProcessesRequest } from '../../../api/gen';
import { ParkingZoneDto } from '../../../api/gen/model/parkingZoneDto';
import { InfrastructureDto } from '../../../api/gen/model/infrastructureDto';
import { DeviceGroupZoneConfigComponent } from './device-group-zone-config/device-group-zone-config.component';
import { ParkingZoneParkingProcessComponent } from './parking-zone-parking-process/parking-zone-parking-process.component';
import { ZoneBarrierCommandsComponent } from './device-group-item-zone-config/zone-barrier/zone-barrier-commands/zone-barrier-commands.component';
import { firstValueFrom } from 'rxjs';
import { RoleService } from '../../auth/role-service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-parking-zone-details',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        JjamesButtonComponent,
        JJamesCardComponent,
        ParkingZoneParkingProcessComponent,
        ZoneBarrierCommandsComponent,
        TranslatePipe
    ],
    templateUrl: './parking-zone-details.component.html',
    styleUrl: './parking-zone-details.component.scss'
})
export class ParkingZoneDetailsComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private parkingZoneService = inject(ParkingZoneService);
    private infrastructureService = inject(InfrastructureService);
    private barrierService = inject(BarrierService);
    private parkingProcessService = inject(ParkingProcessService);
    private dialog = inject(MatDialog);
    private snackBar = inject(MatSnackBar);
    private roleService = inject(RoleService);

    zoneId: string | null = null;
    parkingZone = signal<ParkingZoneDto | null>(null);
    infrastructure = signal<InfrastructureDto | null>(null);
    isLoading = signal(false);
    deviceGroupBarriers = signal<Map<string, BarrierDto[]>>(new Map());
    activeProcesses = signal<ParkingProcessListItemDto[]>([]);
    isAdmin = signal(false);

    // Computed signal to get device groups with their barriers
    deviceGroupsWithBarriers = computed(() => {
        const infra = this.infrastructure();
        const barriersMap = this.deviceGroupBarriers();
        if (!infra?.deviceGroups) return [];

        return infra.deviceGroups.map(dg => ({
            deviceGroup: dg,
            barriers: barriersMap.get(dg.id!) || []
        })).filter(item => item.barriers.length > 0);
    });

    async ngOnInit(): Promise<void> {
        this.zoneId = this.route.snapshot.paramMap.get('id');
        if (this.zoneId) {
            this.loadParkingZone();
        } else {
            this.router.navigate(['/parking-space']);
        }
        this.isAdmin.set(await this.roleService.checkAccess(Role.Administrator));
    }

    private loadParkingZone(): void {
        if (!this.zoneId) return;

        this.isLoading.set(true);
        this.parkingZoneService.parkingZoneParkingZoneIdGet(this.zoneId).subscribe({
            next: (result) => {
                if (result) {
                    this.parkingZone.set(result);
                    if (result.zoneInfrastructureId) {
                        this.loadInfrastructure(result.zoneInfrastructureId);
                    }
                    // Load active processes for Quick Actions
                    this.loadActiveProcesses();
                }
                this.isLoading.set(false);
            },
            error: (error) => {
                console.error('Error loading parking zone:', error);
                this.snackBar.open('Failed to load parking zone details', 'Close', { duration: 3000 });
                this.isLoading.set(false);
            }
        });
    }

    private loadInfrastructure(infrastructureId: string): void {
        this.infrastructureService.infrastructuresInfrastructureIdGet(infrastructureId).subscribe({
            next: (result) => {
                if (result) {
                    this.infrastructure.set(result);
                    // Load barriers for all device groups
                    this.loadBarriersForDeviceGroups(result);
                }
            },
            error: (error) => {
                console.error('Error loading infrastructure:', error);
            }
        });
    }

    private async loadBarriersForDeviceGroups(infrastructure: InfrastructureDto): Promise<void> {
        if (!infrastructure.deviceGroups) return;

        const barriersMap = new Map<string, BarrierDto[]>();

        for (const deviceGroup of infrastructure.deviceGroups) {
            if (deviceGroup.id) {
                try {
                    const barriersReq = await firstValueFrom(
                        this.barrierService.barriersFilterPost({
                            deviceGroupId: deviceGroup.id,
                            pageNumber: 1,
                            pageSize: 100
                        } as ListBarriersRequest)
                    );
                    const items = (barriersReq as BarrierDtoPaginationResult)?.items || [];
                    if (items.length > 0) {
                        barriersMap.set(deviceGroup.id, items);
                    }
                } catch (error) {
                    console.warn(`Could not load barriers for device group ${deviceGroup.id}:`, error);
                }
            }
        }

        this.deviceGroupBarriers.set(barriersMap);
    }

    private loadActiveProcesses(): void {
        if (!this.zoneId) return;

        const request: ListParkingProcessesRequest = {
            parkingZoneId: this.zoneId,
            pageSize: 50,
            pageIndex: 0,
            includeDeleted: false
        };

        this.parkingProcessService.parkingProcessFilterEnrichedPost(request).subscribe({
            next: (result) => {
                const processes = result.items || [];
                // Filter only active processes
                const activeOnly = processes.filter(p => p.isActive);
                this.activeProcesses.set(activeOnly);
            },
            error: (error) => {
                console.error('Error loading active parking processes:', error);
            }
        });
    }

    configureInfrastructure(): void {
        const dialogRef = this.dialog.open(DeviceGroupZoneConfigComponent, {
            width: '800px',
            maxWidth: '95vw',
            data: {
                infrastructure: this.infrastructure()
            }
        });


        dialogRef.afterClosed().subscribe(result => {
            if (result && this.parkingZone()?.zoneInfrastructureId) {
                this.loadInfrastructure(this.parkingZone()!.zoneInfrastructureId!);
            }
        });
    }

    configureDeviceGroup(deviceGroup: DeviceGroupDto): void {
        if (deviceGroup.id) {
            this.router.navigate(['/parking-zone', this.zoneId, 'device-group', deviceGroup.id]);
        }
    }

    goBack(): void {

        this.router.navigate(['/parking-space']);

    }

    editParkingZone(): void {
        if (this.zoneId) {
            this.router.navigate(['/parking-zone/edit', this.zoneId]);
        }
    }
}
